import { Component, ElementRef, ErrorHandler, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable ,  of } from 'rxjs';
import { tap, debounceTime, distinctUntilChanged, switchMap, catchError, map } from 'rxjs/operators';
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import { mergeStates, State } from '../state-view/state.model';
import {
  AutocompleteOption, AutocompleteOptionType, AutocompleteType,
  COUNTRIES
} from '../autocomplete-trigger/autocomplete-trigger.component';
import { AirportAutocompleteResult } from '../models/airport-autocomplete-result.model';
import { AirportService } from '../../airport/airport.service';
import { GoogleService } from '../../google/google.service';
import { State as USState, STATES } from '../../util/states';
import { isString } from '../../util/helpers';
import { Source } from '../models/source.model';
import { SearchFormType } from '../../search/models/search-form-type.model';


@Component({
  selector: 'app-autocomplete-overlay',
  templateUrl: './autocomplete-overlay.component.html',
  styleUrls: ['./autocomplete-overlay.component.scss']
})
export class AutocompleteOverlayComponent implements OnInit {
  @Input() active = false;
  @Input() queryType: AutocompleteType = AutocompleteType.Airports;
  @Input() international = false;
  @Input() currentSearchTab: SearchFormType = SearchFormType.Airports;
  @Output() itemSelected = new EventEmitter<AutocompleteOption>();

  State = State;
  state: State;
  query: string;
  airportSearchState: State = State.Empty;
  locationState: State = State.Empty;
  searchTerm$: Observable<string>;
  airportResults$: Observable<AirportAutocompleteResult[]>;
  googleResults$: Observable<AutocompletePrediction[]>;
  internationalCityResults$: Observable<AutocompletePrediction[]>;
  usStateResults$: Observable<USState[]>;
  airportState: State = State.Empty;
  googleState: State = State.Empty;
  form = new FormGroup ({
    wrapper: new FormGroup({
      type: new FormControl(),
      query: new FormControl()
    })
  });
  isString = isString;
  searchFormType = SearchFormType;

  @ViewChild('queryField', { read: ElementRef }) queryFieldEl: ElementRef;

  get searchState(): State {
    return mergeStates(this.airportSearchState, this.locationState);
  }

  get queryCtrl(): AbstractControl {
    return this.form.get('wrapper').get('query');
  }

  get showAirports(): boolean {
    return (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) && !this.international;
  }

  get showGoogle(): boolean {
    return (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) && !this.international;
  }

  get showInternational(): boolean {
    return this.queryType === AutocompleteType.Combined && this.international;
  }

  get sanitizedQuery(): string {
    return AirportService.sanitizeSearchTerm(this.query);
  }

  constructor(
    private _router: Router,
    private _airportService: AirportService,
    private _googleService: GoogleService,
    private _errorHandler: ErrorHandler
  ) { }

  ngOnInit() {
    this.searchTerm$ = this.queryCtrl.valueChanges.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(300),
      // ignore if next search term is same as previous
      distinctUntilChanged()
    );

    if (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) {
      this.airportResults$ = this.searchTerm$.pipe(
        tap(() => {
          this.airportState = State.Loading;
          this._updateState();
        }),
        switchMap(query => this.queryType === AutocompleteType.Combined
          ? this._airportService.getAirportCompletions(query, 5)
          : this._airportService.getAirportCompletions(query)),
        tap(() => {
          this.airportState = State.Content;
          this._updateState();
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.airportState = State.Error;
          this._updateState();
          return of<AirportAutocompleteResult[]>([]);
        })
      );
    }

    if (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) {
      this.googleResults$ = this.searchTerm$.pipe(
        tap(() => {
          this.googleState = State.Loading;
          this._updateState();
        }),
        switchMap(query => {
          if (!query) {
            return of([]);
          }
          return this._googleService.getPlacePredictions({
            input: query,
            types: ['(cities)'],
            componentRestrictions: {
              country: COUNTRIES
            }
          });
        }),
        tap(() => {
          this.googleState = State.Content;
          this._updateState();
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.googleState = State.Error;
          this._updateState();
          return of<AutocompletePrediction[]>([]);
        })
      );
      // to undo, put after types above
      // sessionToken: new google.maps.places.AutocompleteSessionToken(),
    }

    if (this.queryType === AutocompleteType.Combined) {
      this.usStateResults$ = this.searchTerm$.pipe(
        map(query => {
          if (this.queryType === AutocompleteType.Combined && isString(query)) {
            return this._addUSStates(query);
          }
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.googleState = State.Error;
          this._updateState();
          return of<USState[]>([]);
        })
      );
    }

    if (this.queryType === AutocompleteType.Combined) {
      this.internationalCityResults$ = this.searchTerm$.pipe(
        tap(() => {
          this.googleState = State.Loading;
          this._updateState();
        }),
        switchMap(query => {
          const intResult = this._googleService.getPlacePredictions({
            input: query ? query : ' ',
            types: ['(regions)']
          });
          const intResult2 = intResult.then(intValues => intValues.filter(
            (item) => !item.description.endsWith(', USA')
          ).filter((item2) => item2.types.includes('locality') || item2.types.includes('country')
          ).sort((a, b) => {
            if (a.types.includes('locality') && !b.types.includes('locality')) {
              return -1;
            }
            if (a.types.includes('country') && !b.types.includes('country')) {
              return 1;
            }
            if (a.description < b.description) {
              return -1;
            }
            if (a.description > b.description) {
              return 1;
            }
            return 0;
          }));
          return intResult2;
        }),
        tap(() => {
          this.googleState = State.Content;
          this._updateState();
        }),
        catchError(e => {
          this._errorHandler.handleError(e);
          this.googleState = State.Error;
          this._updateState();
          return of<AutocompletePrediction[]>([]);
        })
      );
    }

  }

  private _addUSStates(query: string): Array<USState> {
    let filteredStates = [];
    if (query.length < 2) {
      return filteredStates;
    }
    if (query.length === 2) {
      filteredStates = STATES.filter(item => item.stateCode.toLowerCase().startsWith(query.toLowerCase()));
    } else {
      filteredStates = STATES.filter(item => item.name.toLowerCase().startsWith(query.toLowerCase()));
    }
    return filteredStates;
  }

  show() {
    this.airportSearchState = State.Empty;
    this.locationState = State.Empty;
    this.queryCtrl.setValue('');
    this.active = true;
  }

  focus() {
    this.queryFieldEl.nativeElement.focus();
  }

  hide() {
    this.queryFieldEl.nativeElement.blur();
    this.active = false;
  }

  onQueryBlur() {
    setTimeout(() => this.hide(), 300);
  }

  private _updateState() {
    this.state = mergeStates(this.airportState, this.googleState);
  }

  getAirportText(result: AirportAutocompleteResult): string {
    return `${result.icaoId || result.faaId || ''}: ${result.name}`;
  }

  getCityStateText(result: AirportAutocompleteResult): string {
    const city = result.city;
    const state = result.stateProvince;
    if (city || state) {
      return '(' + [city, state].filter(v => v).join(', ') + ')';
    }
    return '';
  }

  getCityText(): string {
    if (this.queryType === AutocompleteType.Combined) {
      return ' (City)';
    }
    return '';
  }

  getStateTerritoryText(): string {
    return ' (State/Territory)';
  }

  getInternationalText(result: AutocompletePrediction): string {
    if (result.types.includes('country')) {
      return ' (Country)';
    }
    if (result.types.includes('locality')) {
      return ' (City)';
    }
    return '';
  }

  onSelectAirport(airport: AirportAutocompleteResult) {
    this.itemSelected.emit({
      type: AutocompleteOptionType.Airport,
      airport
    });
    this.hide();
  }

  onSelectLocation(location: AutocompletePrediction) {
    this.itemSelected.emit({
      type: AutocompleteOptionType.Location,
      location
    });
    this.hide();
  }

  onSelectUSState(state: USState) {
    this.itemSelected.emit({
      type: AutocompleteOptionType.State,
      state
    });
    this.hide();
  }

  async onSubmit() {
    const term = this.sanitizedQuery.toUpperCase();
    if (term) {
      try {
        this.state = State.Loading;
        const result = await this._airportService.getAirport(term);
        if (result) {
          if (result.sourceId === Source.CaribbeanGuide || result.sourceId === Source.BahamasGuide) {
            this._router.navigate(['/airports',  term, 'island']);
          } else {
            this._router.navigate(['/airports', term]);
          }
          this.hide();
          return;
        }
      } catch (e) {
        this._errorHandler.handleError(e);
        this.state = State.Error;
        return;
      }
    }
    this.itemSelected.emit({
      type: AutocompleteOptionType.Text,
      query: this.sanitizedQuery
    });
    this.hide();
  }
}
